import { useMutation, UseMutationOptions, useQueryClient } from "@tanstack/react-query";
import { FormState } from "pages/app/OnboardingPage";

import { useAssertCurrentUser } from "lib/hooks/api/users/useCurrentUser";
import { EventNames, useTracking } from "lib/tracking";
import { useUpdateSignalSettings } from "../signals/useUpdateSignalSettings";
import { useUpdateCurrentUser } from "../users/useUpdateCurrentUser";
import { useCompleteOnboarding } from "./useCompleteOnboarding";
import { usePopulateTeamMetadata } from "./usePopulateTeamMetadata";
import { useUpdatePubSecExperience } from "./useUpdatePubSecExperience";

type useSubmitOnboardingParams = {
  formState: FormState;
  shouldRefreshFeed: boolean;
  allowEmptyKeywords?: boolean;
};

export function useSubmitOnboarding(
  options?: UseMutationOptions<void, unknown, useSubmitOnboardingParams, unknown>,
) {
  const user = useAssertCurrentUser();

  const { logEvent } = useTracking();

  const queryClient = useQueryClient();
  const { mutateAsync: completeOnboarding } = useCompleteOnboarding();
  const { mutateAsync: populateTeamMetadata } = usePopulateTeamMetadata();
  const { mutateAsync: updateSignalSettings } = useUpdateSignalSettings();
  const { mutateAsync: updatePubSecExperience } = useUpdatePubSecExperience();

  const { mutate: updateCurrentUser } = useUpdateCurrentUser();

  return useMutation(
    async ({
      formState,
      shouldRefreshFeed,
      allowEmptyKeywords = false,
    }: useSubmitOnboardingParams) => {
      await completeOnboarding({});

      updateCurrentUser({
        request: {
          guid: user.guid,
          completedOnboarding: true,
        },
      });

      // Recording pub-sec experience is a non-critical side-effect — don't let a
      // blip (e.g. a transient Redis error) abort onboarding completion and the
      // signal-settings creation / redirect that follow.
      try {
        await updatePubSecExperience({ pubSecExperience: formState.experience });
      } catch {
        // Already reported by the app-wide mutation error handler.
      }

      // Only persist signal settings when the user went through keyword selection
      // (shouldRefreshFeed) or is on the empty-keyword onboarding variant.
      if (shouldRefreshFeed || allowEmptyKeywords) {
        const keywords = allowEmptyKeywords ? [] : formState.keywords;

        await updateSignalSettings({
          input: {
            countries: formState.countries,
            languages: formState.languages,
            keywords,
            ...(allowEmptyKeywords && { allowEmptyKeywords: true }),
          },
        });

        logEvent(EventNames.recordQueryCreated, {
          Countries: formState.countries,
          Keywords: keywords,
        });

        if (allowEmptyKeywords) {
          try {
            await populateTeamMetadata({});
            await queryClient.invalidateQueries(["team"]);
          } catch {
            // Already reported by the app-wide mutation error handler.
          }
        }
      }
    },
    options,
  );
}
